import React, { useEffect, useState } from "react";
import { Copy, CheckCircle2, BookOpen, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * EHRCopyButton — one-tap copy of any ZidaMD AI output formatted for the
 * physician's chosen EHR. Includes a "Paste guide" modal with step-by-step
 * instructions per EHR system (Epic, Cerner, athena, eClinicalWorks, Meditech).
 */
export default function EHRCopyButton({ text, label = "Copy for EHR", module = "generic" }) {
  const [open, setOpen] = useState(false);
  const [guides, setGuides] = useState([]);
  const [activeGuide, setActiveGuide] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/ehr/paste-guides").then(r => setGuides(r.data || [])).catch(() => {});
  }, []);

  const copy = async (ehrKey) => {
    try { await navigator.clipboard.writeText(text || ""); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
    toast.success(`Copied — open ${ehrKey} and paste with Ctrl+V`);
    // Log a usage event so the click-burden counter increments.
    try { await api.post("/time-saved/log", { module: `ehr_copy_${ehrKey}`, minutes_saved: 2 }); } catch { /* ignore */ }
  };

  const openGuide = async (ehrKey) => {
    try {
      const r = await api.get(`/ehr/paste-guide/${ehrKey}`);
      setActiveGuide(r.data);
    } catch { toast.error("Guide unavailable"); }
  };

  return (
    <div className="inline-block relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-primary text-white text-xs font-medium hover:bg-[#35096B]"
        data-testid={`ehr-copy-${module}`}
      >
        {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        {label}
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-60 bg-white border border-brand rounded-md shadow-lg p-2" data-testid={`ehr-copy-menu-${module}`}>
          <div className="text-[10px] uppercase tracking-wide text-brand-muted px-2 py-1">Copy formatted for…</div>
          {guides.map(g => (
            <div key={g.key} className="flex items-center justify-between gap-1">
              <button
                onClick={() => { copy(g.key); setOpen(false); }}
                className="flex-1 text-left text-xs px-2 py-1.5 rounded hover:bg-brand-bg"
                data-testid={`ehr-copy-${g.key}`}
              >
                {g.name}
              </button>
              <button
                onClick={() => openGuide(g.key)}
                className="p-1 rounded hover:bg-brand-bg text-brand-muted"
                title="Paste guide"
                data-testid={`ehr-guide-${g.key}`}
              >
                <BookOpen className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      {activeGuide && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60" onClick={() => setActiveGuide(null)} data-testid="ehr-guide-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="eyebrow">EHR Paste Guide</div>
                <h3 className="text-lg font-semibold">{activeGuide.name}</h3>
              </div>
              <button onClick={() => setActiveGuide(null)} data-testid="ehr-guide-close"><X className="w-4 h-4 text-brand-muted" /></button>
            </div>
            <ol className="space-y-2 text-sm text-brand-text">
              {activeGuide.instructions.map((step, i) => (
                <li key={i} className="flex gap-2"><span className="text-brand-primary font-semibold">{i + 1}.</span><span>{step}</span></li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
